import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to set GA4 User ID
const setGA4UserId = (userId) => {
  window.dataLayer = window.dataLayer || [];
  // Clear previous user properties
  window.dataLayer.push({ user_id: null });
  // Set new user_id
  if (userId) {
    window.dataLayer.push({
      event: 'user_login_state',
      user_id: userId.toString(),
      user_properties: {
        user_id: userId.toString()
      }
    });
    // Also set for gtag if available
    if (typeof window.gtag === 'function') {
      window.gtag('set', 'user_id', userId.toString());
    }
  }
};

// Helper function to clear GA4 User ID on logout
const clearGA4UserId = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'user_logout',
    user_id: null,
    user_properties: null
  });
  if (typeof window.gtag === 'function') {
    window.gtag('set', 'user_id', null);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 初始化 - 檢查 localStorage 中的 token
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          // 驗證 token 是否有效
          const response = await api.get('/auth/verify', {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (response.data.valid) {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
            // Set GA4 User ID
            setGA4UserId(parsedUser.id);
          } else {
            logout();
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          // Token 無效時直接使用本地資料（開發環境）
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setIsAuthenticated(true);
          // Set GA4 User ID
          setGA4UserId(parsedUser.id);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // 手機登入/註冊 (簡訊驗證碼)
  const loginWithPhone = async (phone, verificationCode) => {
    try {
      const response = await api.post('/auth/phone', { phone, code: verificationCode });

      if (response.data.success) {
        const { token, user: userData } = response.data;
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('verified_phone', phone);
        localStorage.setItem('user_phone', phone); // 用於訂單查詢
        setUser(userData);
        setIsAuthenticated(true);
        // Set GA4 User ID
        setGA4UserId(userData.id);
        return { success: true, user: userData };
      }

      return { success: false, error: response.data.error || '登入失敗' };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || '登入失敗' };
    }
  };

  // 密碼登入
  const loginWithPassword = async (phone, password) => {
    try {
      const response = await api.post('/auth/login-password', { phone, password });

      if (response.data.success) {
        const { token, user: userData } = response.data;
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('verified_phone', phone);
        localStorage.setItem('user_phone', phone); // 用於訂單查詢
        setUser(userData);
        setIsAuthenticated(true);
        // Set GA4 User ID
        setGA4UserId(userData.id);
        return { success: true, user: userData };
      }

      return { success: false, error: response.data.error || '登入失敗' };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || '登入失敗' };
    }
  };

  // 註冊（手機 + 密碼）
  const registerWithPassword = async (phone, password, name) => {
    try {
      const response = await api.post('/auth/register', { phone, password, name });

      if (response.data.success) {
        const { token, user: userData } = response.data;
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('verified_phone', phone);
        localStorage.setItem('user_phone', phone); // 用於訂單查詢
        setUser(userData);
        setIsAuthenticated(true);
        // Set GA4 User ID
        setGA4UserId(userData.id);
        return { success: true, user: userData };
      }

      return { success: false, error: response.data.error || '註冊失敗' };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || '註冊失敗' };
    }
  };

  // 檢查登入方式
  const checkLoginMethod = async (phone) => {
    try {
      const response = await api.post('/auth/check-login-method', { phone });
      return response.data;
    } catch (error) {
      return { exists: false, hasPassword: false, error: error.message };
    }
  };

  // 設定/修改密碼
  const setPassword = async (phone, code, newPassword) => {
    try {
      const response = await api.post('/auth/set-password', { phone, code, newPassword });
      if (response.data.success) {
        return { success: true, message: response.data.message };
      }
      return { success: false, error: response.data.error };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || '設定失敗' };
    }
  };

  // 直接用 token 和 user 設定登入狀態 (給結帳頁用)
  const setAuthFromCheckout = (token, userData) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('verified_phone', userData.phone);
    localStorage.setItem('user_phone', userData.phone); // 用於訂單查詢
    setUser(userData);
    setIsAuthenticated(true);
    // Set GA4 User ID
    setGA4UserId(userData.id);
  };

  // 發送手機驗證碼
  const sendPhoneCode = async (phone) => {
    try {
      const response = await api.post('/auth/phone/send-code', { phone });
      if (response.data.success) {
        return {
          success: true,
          devCode: response.data.code // 開發環境會回傳驗證碼
        };
      }
      return { success: false, error: response.data.error || '發送失敗' };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || '發送失敗' };
    }
  };

  // Google 登入
  const loginWithGoogle = async (googleToken) => {
    try {
      const response = await api.post('/auth/google', { token: googleToken });
      const { token, user: userData } = response.data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);
      // Set GA4 User ID
      setGA4UserId(userData.id);

      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Google 登入失敗' };
    }
  };

  // LINE 登入
  const loginWithLine = async (lineCode) => {
    try {
      const response = await api.post('/auth/line', { code: lineCode });
      const { token, user: userData } = response.data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);
      // Set GA4 User ID
      setGA4UserId(userData.id);

      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'LINE 登入失敗' };
    }
  };

  // 初始化 LINE Login URL
  const getLineLoginUrl = () => {
    const clientId = process.env.REACT_APP_LINE_CLIENT_ID || 'YOUR_LINE_CLIENT_ID';
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/line/callback`);
    const state = Math.random().toString(36).substring(7);
    localStorage.setItem('lineLoginState', state);

    return `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&scope=profile%20openid%20email`;
  };

  // 登出
  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('user_phone');
    localStorage.removeItem('verified_phone');
    setUser(null);
    setIsAuthenticated(false);
    // Clear GA4 User ID
    clearGA4UserId();
  };

  // 更新會員資料
  const updateProfile = async (profileData) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await api.put('/auth/profile', profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      return { success: true, user: updatedUser };
    } catch (error) {
      // 開發環境模擬
      if (process.env.NODE_ENV === 'development' || !error.response) {
        const updatedUser = { ...user, ...profileData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      }
      return { success: false, error: error.response?.data?.message || '更新失敗' };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    loginWithPhone,
    loginWithPassword,
    registerWithPassword,
    checkLoginMethod,
    setPassword,
    sendPhoneCode,
    loginWithGoogle,
    loginWithLine,
    getLineLoginUrl,
    logout,
    updateProfile,
    setAuthFromCheckout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
